import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import { Add, ManageAccounts } from '@mui/icons-material';
import { userService } from '../services/api';
import { usePortalText } from '../locales/portalTranslations';

const emptyForm = { username: '', email: '', role: 'supervisor' };
const roleLabel = (role, pt) => role === 'admin' ? pt('systemAdmin') : role === 'supervisor' ? pt('supervisor') : pt('userRole');

export default function UserManagementPage() {
  const pt = usePortalText();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [form, setForm] = useState(emptyForm);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await userService.list();
      setUsers(data.users || []);
    } catch (requestError) {
      setError(requestError.response?.data?.message || pt('usersLoadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const createUser = async event => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const data = await userService.create(form);
      setForm(emptyForm);
      setOpen(false);
      setNotice(data.invitationSent ? pt('invitationSent') : pt('invitationFailed'));
      await loadUsers();
    } catch (requestError) {
      setError(requestError.response?.data?.message || pt('userCreateError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} mb={3}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ManageAccounts color="primary" />
            <Typography variant="h4" fontWeight={700}>{pt('userManagement')}</Typography>
          </Stack>
          <Typography color="text.secondary" mt={0.5}>{pt('userManagementHelp')}</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setError(''); setNotice(''); setOpen(true); }}>
          {pt('createUser')}
        </Button>
      </Stack>

      {error && !open && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity={notice === pt('invitationSent') ? 'success' : 'warning'} sx={{ mb: 2 }}>{notice}</Alert>}
      <TableContainer component={Paper} variant="outlined">
        {loading ? (
          <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress size={32} /></Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{pt('username')}</TableCell>
                <TableCell>{pt('email')}</TableCell>
                <TableCell>{pt('role')}</TableCell>
                <TableCell>{pt('accountStatus')}</TableCell>
                <TableCell>{pt('createdColumn')}</TableCell>
                <TableCell>{pt('lastSignIn')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map(user => (
                <TableRow key={user.id} hover>
                  <TableCell><Typography fontWeight={600}>{user.username}</Typography></TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell><Chip size="small" color={user.role === 'admin' ? 'primary' : 'default'} label={roleLabel(user.role, pt)} /></TableCell>
                  <TableCell><Chip size="small" color={user.invitation_pending ? 'warning' : 'success'} variant="outlined" label={user.invitation_pending ? pt('invited') : pt('active')} /></TableCell>
                  <TableCell>{new Date(user.created_at).toLocaleString()}</TableCell>
                  <TableCell>{user.last_login_at ? new Date(user.last_login_at).toLocaleString() : pt('never')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog open={open} onClose={saving ? undefined : () => setOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={createUser}>
          <DialogTitle>{pt('createUser')}</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Stack spacing={2} mt={1}>
              <TextField required autoFocus label={pt('username')} value={form.username} inputProps={{ minLength: 3, maxLength: 50 }} onChange={event => setForm({ ...form, username: event.target.value })} />
              <TextField required type="email" label={pt('email')} value={form.email} onChange={event => setForm({ ...form, email: event.target.value })} />
              <FormControl required>
                <InputLabel>{pt('role')}</InputLabel>
                <Select label={pt('role')} value={form.role} onChange={event => setForm({ ...form, role: event.target.value })}>
                  <MenuItem value="user">{pt('userRole')}</MenuItem>
                  <MenuItem value="supervisor">{pt('supervisor')}</MenuItem>
                  <MenuItem value="admin">{pt('systemAdmin')}</MenuItem>
                </Select>
              </FormControl>
              <Alert severity="info">{pt('invitationHelp')}</Alert>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={() => setOpen(false)} disabled={saving}>{pt('cancel')}</Button>
            <Button type="submit" variant="contained" disabled={saving}>{saving ? pt('creating') : pt('createUser')}</Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}
